import React, { useMemo, useState } from 'react';
import { Map, X } from 'lucide-react';
import { Switch } from '@/shared/ui/switch';
import { PhotoMarker } from '../types/types';
import { useMapViewport } from '../hooks/useMapViewport';
import { cn } from '@/shared/utils/utils';
import {
  PANORAMA_MAP_BUTTON_WRAPPER_TOP_CLASS,
  PANORAMA_TABLET_DIALOG_CLOSE_CLASS,
  PANORAMA_TABLET_FLOATING_BUTTON_BOX,
  PANORAMA_TABLET_FLOATING_ICON,
} from '../constants/constants';

interface LayoutMapPreviewProps {
  photoId: number;
  markerNumber?: number;
  layoutName?: string;
  layoutType?: string;
  layoutImageUrl?: string;
  layoutFlippedHorizontal?: boolean;
  layoutFlippedVertical?: boolean;
  layoutImageDimensions?: { width: number; height: number };
  photoMarkers?: PhotoMarker[];
  onNavigateToPhotoId?: (photoId: number) => void;
}

const LayoutMapPreview: React.FC<LayoutMapPreviewProps> = ({
  photoId,
  markerNumber,
  layoutName,
  layoutType,
  layoutImageUrl,
  layoutFlippedHorizontal = false,
  layoutFlippedVertical = false,
  layoutImageDimensions,
  photoMarkers = [],
  onNavigateToPhotoId,
}) => {
  const [open, setOpen] = useState(false);
  const [showAllMarkers, setShowAllMarkers] = useState(false);
  const {
    zoom,
    pan,
    isPanning,
    resetViewport,
    handleWheel,
    handleMouseDown,
    handleMouseUp,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
  } = useMapViewport();

  const activeMarker = useMemo(
    () =>
      photoMarkers.find((marker) =>
        markerNumber != null
          ? marker.number === markerNumber
          : marker.photos?.some((photo) => photo.id === photoId),
      ),
    [photoId, markerNumber, photoMarkers],
  );

  if (!layoutImageUrl || !layoutImageDimensions?.width || !layoutImageDimensions?.height) {
    return null;
  }

  const handleToggle = () => {
    setOpen((prev) => {
      const next = !prev;
      if (next) {
        resetViewport();
        setShowAllMarkers(false);
      }
      return next;
    });
  };

  return (
    <>
      {!open && (
        <div
          className={cn('fixed z-[1000]', PANORAMA_MAP_BUTTON_WRAPPER_TOP_CLASS)}
          style={{
            right: 'max(0.75rem, env(safe-area-inset-right, 0px))',
          }}
        >
          <button
            type="button"
            onClick={handleToggle}
            className={cn(
              'h-9 w-9 rounded-full border border-white/30 bg-black/55 text-white flex items-center justify-center hover:bg-black/80 transition-colors',
              PANORAMA_TABLET_FLOATING_BUTTON_BOX,
            )}
            title="Show layout map"
            aria-label="Show layout map"
          >
            <Map className={cn('h-4 w-4', PANORAMA_TABLET_FLOATING_ICON)} />
          </button>
        </div>
      )}

      {open && (
        <div className="absolute inset-0 z-[1100] bg-black/65 backdrop-blur-[2px] flex items-center justify-center p-6">
          <div className="w-full max-w-6xl h-[82vh] rounded-xl border border-black/10 bg-white/95 p-3 flex flex-col">
            <div className="flex items-center justify-between mb-3 px-1 gap-3">
              <p className="text-sm text-slate-700">
                {layoutName || 'Current Layout'}
                {layoutType ? ` (${layoutType})` : ''}
              </p>
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-2 text-xs text-slate-600 select-none">
                  <Switch checked={showAllMarkers} onCheckedChange={setShowAllMarkers} />
                  <span className="hidden sm:inline">Show all markers</span>
                  <span className="sm:hidden">All markers</span>
                </div>
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  className={cn(
                    'h-8 w-8 rounded-full border border-white/30 bg-black/50 text-white flex items-center justify-center hover:bg-black/80 transition-colors',
                    PANORAMA_TABLET_DIALOG_CLOSE_CLASS,
                  )}
                  title="Close layout map"
                  aria-label="Close layout map"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>

            <div
              className={`relative flex-1 overflow-hidden rounded-lg bg-white touch-none ${isPanning ? 'cursor-grabbing' : 'cursor-grab'}`}
              onWheel={handleWheel}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
              onMouseDown={handleMouseDown}
              onMouseUp={handleMouseUp}
            >
              <div className="absolute inset-0 flex items-center justify-center">
                <div
                  className="relative inline-block"
                  style={{
                    transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
                    transformOrigin: 'center center',
                  }}
                >
                  <img
                    src={layoutImageUrl}
                    alt="Associated layout map"
                    className="max-w-[88vw] max-h-[70vh] object-contain block"
                    style={{
                      transform: `scale(${layoutFlippedHorizontal ? -1 : 1}, ${layoutFlippedVertical ? -1 : 1})`,
                    }}
                    draggable={false}
                  />
                  {photoMarkers.map((marker) => {
                    const left = (marker.x / layoutImageDimensions.width) * 100;
                    const top = (marker.y / layoutImageDimensions.height) * 100;
                    const isActive = activeMarker?.id === marker.id;
                    if (!isActive && !showAllMarkers) return null;
                    return (
                      <div
                        key={marker.id}
                        className={`absolute -translate-x-1/2 -translate-y-1/2 ${
                          marker.photos?.length ? 'cursor-pointer' : 'cursor-default'
                        }`}
                        style={{ left: `${left}%`, top: `${top}%` }}
                        onClick={() => {
                          if (!onNavigateToPhotoId) return;
                          const markerPhotoId = marker.photos?.[0]?.id;
                          if (!markerPhotoId || markerPhotoId === photoId) return;
                          onNavigateToPhotoId(markerPhotoId);
                        }}
                        title={marker.photos?.length ? `Open marker ${marker.number}` : `Marker ${marker.number} has no photos`}
                      >
                        {isActive && <span className="absolute -inset-1.5 sm:-inset-3 rounded-full bg-blue-400/50 animate-ping" />}
                        <span
                          className={`relative flex h-3 min-w-3 px-0.5 sm:h-5 sm:min-w-5 sm:px-1 items-center justify-center rounded-full border text-[7px] sm:text-[10px] font-semibold leading-none ${
                            isActive
                              ? 'bg-blue-500 text-white border-blue-200 shadow-[0_0_0_1.5px_rgba(59,130,246,0.35)] sm:shadow-[0_0_0_4px_rgba(59,130,246,0.35)]'
                              : 'bg-red-500 text-white border-red-200'
                          }`}
                        >
                          {marker.number}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
              <div className="absolute bottom-3 right-3 px-2.5 py-1 rounded-md border border-slate-300 bg-white/90 text-slate-700 text-xs">
                {Math.round(zoom * 100)}%
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default LayoutMapPreview;
